import { DatePicker, IStyles, pxToRem, pxTovW } from '@geneo2-web/shared-ui';
import {
  Box,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  disableEndTime,
  disableStartTime,
  wrongEndTime,
  wrongStartTime,
} from '../../../utils/assessment.utils';
import {
  DesktopTimePicker,
  LocalizationProvider,
  TimeView,
  MobileTimePicker,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Dispatch, SetStateAction } from 'react';

const styles: IStyles = {
  root: {
    display: 'flex',
    gap: '2vw',
    flexDirection: { xs: 'column', lg: 'row' },
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  inputBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(10), md: pxTovW(10) },
  },
  labelTypo: {
    fontSize: { xs: pxToRem(20), md: pxTovW(25), lg: pxTovW(18) },
  },
  timeWithErrorBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(5), md: pxTovW(10) },
  },
  timePicker: {
    border: '1px solid #CCE6FE',
    borderRadius: { xs: pxToRem(15), md: pxTovW(15) },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    paddingX: { xs: pxToRem(15), md: pxTovW(15) },
    boxSizing: 'border-box',
    width: { xs: pxToRem(124), md: pxTovW(240), lg: pxTovW(180) },
    height: { xs: pxToRem(65), md: pxTovW(75), lg: pxTovW(65) },
    '& .MuiOutlinedInput-input': {
      fontSize: { xs: pxToRem(15), md: pxTovW(28), lg: pxTovW(21) },
    },
    '& .MuiOutlinedInput-root.Mui-error': {
      border: 'none',
    },
    '& .MuiFormHelperText-root.Mui-error': {
      color: 'transparent',
    },
    '& .MuiSvgIcon-root': {
      fontSize: { xs: pxToRem(15), md: pxTovW(45), lg: pxTovW(28) },
      borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
      transition: 'background 0.4s ease-in-out',
      '&:hover': {
        backgroundColor: '#E6F3FB',
      },
    },
  },

  mobilePaper: {
    '& .MuiTimePickerToolbar-ampmLabel': {
      fontWeight: 500,
      borderRadius: { xs: pxToRem(5), md: pxTovW(5) },
      px: { xs: pxToRem(5), md: pxTovW(5) },
    },
    '& .MuiTimePickerToolbar-ampmLabel.Mui-selected': {
      color: '#ffffff !important',
      backgroundColor: '#007CDC',
    },
    '& .MuiPickersToolbarText-root.Mui-selected': {
      color: '#007CDC',
      fontWeight: 800,
      borderBottom: '1px solid #007CDC',
    },
  },
};

interface IProps {
  startTimeValues: { timeValue: string; dateValue: string };
  setStartTimeValues: Dispatch<
    SetStateAction<{ timeValue: string; dateValue: string }>
  >;
  endTimeValues?: { timeValue: string; dateValue: string };
  setEndTimeValues?: Dispatch<
    SetStateAction<{ timeValue: string; dateValue: string }>
  >;
}
export const StartAndEndPointPicker = (props: IProps) => {
  const {
    startTimeValues,
    endTimeValues,
    setStartTimeValues,
    setEndTimeValues,
  } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const StartEndPicker = isMobile ? MobileTimePicker : DesktopTimePicker;

  // ------------------------------------------------------------

  function formatTime(timeValue: string) {
    // Remove spaces and ensure two digits for hours and minutes
    const [hoursRaw, minutesRaw] = timeValue.split(':');
    const hours = hoursRaw.trim().padStart(2, '0');
    const minutes = (minutesRaw || '0').trim().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  // ------------------------------------------------------------

  return (
    <Box sx={styles.root}>
      <Box sx={styles.inputBox}>
        <Typography variant="h3" sx={styles.labelTypo}>
          Start Date
        </Typography>
        <Box sx={{ display: 'flex', gap: '1vw' }}>
          <Box sx={styles.timeWithErrorBox}>
            <DatePicker
              value={startTimeValues.dateValue}
              handleDateChange={(date) => {
                setStartTimeValues({ ...startTimeValues, dateValue: date });
              }}
            />
            {wrongStartTime({ startTimeValues }).wrongDate ? (
              <Typography
                variant="h4"
                textAlign="center"
                sx={{ color: '#F54040' }}
              >
                Invalid Date
              </Typography>
            ) : null}
          </Box>

          {/* <TimePicker
          date={startTimeValues.dateValue}
          value={startTimeValues.timeValue}
          handleTimeChange={(time) => {
            setStartTimeValues({ ...startTimeValues, timeValue: time });
          }}
          disabled={startTimeValues.dateValue === '' ? true : false}
        /> */}

          <Box sx={styles.timeWithErrorBox}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <StartEndPicker
                ampm
                closeOnSelect={false}
                value={
                  new Date(
                    `${startTimeValues.dateValue.split('T')[0]}T${formatTime(
                      startTimeValues.timeValue
                    )}`
                  )
                }
                onChange={(newValue: Date | null) => {
                  if (newValue) {
                    const hours = String(newValue.getHours()).padStart(2, '0');
                    const minutes = String(newValue.getMinutes()).padStart(
                      2,
                      '0'
                    );
                    setStartTimeValues({
                      ...startTimeValues,
                      timeValue: `${hours}:${minutes}`,
                    });
                  }
                }}
                shouldDisableTime={(value: Date, view: TimeView) =>
                  disableStartTime({
                    value: value,
                    view: view,
                    startTimeValues: startTimeValues,
                  })
                }
                slots={{ textField: TextField }}
                slotProps={{
                  textField: { sx: styles.timePicker },
                  mobilePaper: { sx: styles.mobilePaper },
                }}
              />
            </LocalizationProvider>

            {wrongStartTime({ startTimeValues }).wrongTime ? (
              <Typography
                variant="h4"
                textAlign="center"
                sx={{ color: '#F54040' }}
              >
                Invalid Time
              </Typography>
            ) : null}
          </Box>
        </Box>
      </Box>
      {endTimeValues && setEndTimeValues ? (
        <Box sx={styles.inputBox}>
          <Typography variant="h3" sx={styles.labelTypo}>
            End Date
          </Typography>
          <Box sx={{ display: 'flex', gap: '1vw' }}>
            <Box sx={styles.timeWithErrorBox}>
              <DatePicker
                minDate={startTimeValues.dateValue.slice(0, 10)}
                value={endTimeValues.dateValue}
                handleDateChange={(date) => {
                  setEndTimeValues({ ...endTimeValues, dateValue: date });
                }}
              />

              {wrongEndTime({ startTimeValues, endTimeValues }).wrongDate ? (
                <Typography
                  variant="h4"
                  textAlign="center"
                  sx={{ color: '#F54040' }}
                >
                  Invalid Date
                </Typography>
              ) : null}
            </Box>

            {/* <TimePicker
          disabled={
            startTimeValues.timeValue === '' ||
            endTimeValues.dateValue === ''
              ? true
              : false
          }
          minTime={
            startTimeValues.dateValue === endTimeValues.dateValue
              ? startTimeValues.timeValue
              : undefined
          }
          date={endTimeValues.dateValue}
          value={endTimeValues.timeValue}
          handleTimeChange={(time) => {
            setEndTimeValues({ ...endTimeValues, timeValue: time });
          }}
        /> */}

            <Box sx={styles.timeWithErrorBox}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <StartEndPicker
                  ampm
                  closeOnSelect={false}
                  disabled={
                    startTimeValues.timeValue === '' ||
                    endTimeValues.dateValue === ''
                      ? true
                      : false
                  }
                  value={
                    new Date(
                      `${endTimeValues.dateValue.split('T')[0]}T${formatTime(
                        endTimeValues.timeValue
                      )}`
                    )
                  }
                  onChange={(newValue: Date | null) => {
                    if (newValue) {
                      const hours = String(newValue.getHours()).padStart(
                        2,
                        '0'
                      );
                      const minutes = String(newValue.getMinutes()).padStart(
                        2,
                        '0'
                      );
                      setEndTimeValues({
                        ...endTimeValues,
                        timeValue: `${hours}:${minutes}`,
                      });
                    }
                  }}
                  shouldDisableTime={(value: Date, view: TimeView) =>
                    disableEndTime({
                      value: value,
                      view: view,
                      startTimeValues: startTimeValues,
                      endTimeValues: endTimeValues,
                    })
                  }
                  slots={{ textField: TextField }}
                  slotProps={{
                    textField: { sx: styles.timePicker },
                    mobilePaper: { sx: styles.mobilePaper },
                  }}
                />
              </LocalizationProvider>

              {wrongEndTime({ startTimeValues, endTimeValues }).wrongTime ? (
                <Typography
                  variant="h4"
                  textAlign="center"
                  sx={{ color: '#F54040' }}
                >
                  Invalid Time
                </Typography>
              ) : null}
            </Box>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};
